import React, { useEffect, useState } from "react";
import cn from "classnames";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Card from "../../../components/Card";
import Icon from "../../../components/Icon";
import { getUsers } from "../../../api/user.service";
import styles from "./Kpis.module.sass";

const fmt = (n) =>
  n == null
    ? "—"
    : n >= 1_000_000
    ? (n / 1_000_000).toFixed(1).replace(".", ",") + " M"
    : n >= 100_000
    ? Math.round(n / 1000).toLocaleString("es-ES") + " k"
    : n.toLocaleString("es-ES");

const fmtEur = (n) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(n);

const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("") || "?";

const gradients = [
  ["#FFBC99", "#FF8A65"],
  ["#CABDFF", "#8E59FF"],
  ["#B1E5FC", "#2A85FF"],
  ["#B5E4CA", "#83BF6E"],
  ["#FFD88D", "#FFB020"],
  ["#FFA5D8", "#FF6AC2"],
];
const swatchFor = (str = "") => {
  let h = 0;
  for (const c of str) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
  const [a, b] = gradients[Math.abs(h) % gradients.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
};

/**
 * KPI header del Home: usuarios, DAU/MAU, subs de pago, MRR estimado y partidas
 * de hoy, con la tira de últimos clientes debajo.
 */
const Kpis = ({ className }) => {
  const { t } = useTranslation();
  const { value, isLoading, error } = useSelector((s) => s.stats.overview);
  const gamesDaily = useSelector((s) => s.stats.gamesDaily.value);
  const loading = isLoading && !value;

  const users = value?.users ?? {};
  const premium = value?.premium ?? {};
  const content = value?.content ?? {};
  const texts = value?.texts ?? {};

  const days = gamesDaily?.days ?? [];
  const spark = days.slice(-14).map((d) => d.count);
  const sparkMax = Math.max(...spark, 1);
  const gamesToday = days.length ? days[days.length - 1].count : null;
  const gamesYesterday = days.length > 1 ? days[days.length - 2].count : null;
  const gamesDelta =
    gamesToday != null && gamesYesterday
      ? Math.round(((gamesToday - gamesYesterday) / gamesYesterday) * 100)
      : null;

  const dauPct =
    users.activeLast24h != null && users.activeLast30d
      ? Math.round((users.activeLast24h / users.activeLast30d) * 100)
      : null;

  const tiles = [
    {
      key: "users",
      icon: "profile-circle",
      color: "#B1E5FC",
      label: t("home.kpis.users", "Usuarios"),
      value: fmt(users.total),
      to: "/users",
      sub:
        users.newLast30d != null
          ? `+${fmt(users.newLast30d)} ${t("home.kpis.thisMonth", "este mes")}`
          : null,
      tone: users.newLast30d > 0 ? "up" : "flat",
    },
    {
      key: "dau",
      icon: "activity",
      color: "#B5E4CA",
      label: t("home.kpis.dau", "DAU · activos 24 h"),
      value: fmt(users.activeLast24h),
      sub: dauPct != null ? `${dauPct}% ${t("home.kpis.ofMau", "del MAU")}` : null,
      tone: "flat",
    },
    {
      key: "mau",
      icon: "donut-chart",
      color: "#CABDFF",
      label: t("home.kpis.mau", "MAU · activos 30 d"),
      value: fmt(users.activeLast30d),
      sub:
        users.activeLast7d != null
          ? `${t("home.kpis.wau", "WAU")} ${fmt(users.activeLast7d)}`
          : null,
      tone: "flat",
    },
    {
      key: "subs",
      icon: "diamond",
      color: "#FFD88D",
      label: t("home.kpis.paying", "Subs de pago"),
      value: fmt(premium.payingSubscribers ?? premium.activeSubscriptions),
      to: "/subscriptions",
      sub:
        premium.trialSubscriptions > 0
          ? `${premium.trialSubscriptions} ${t("home.kpis.inTrial", "en trial")}`
          : premium.newLast30d != null
          ? `+${fmt(premium.newLast30d)} ${t("home.kpis.last30d", "en 30 d")}`
          : null,
      tone: premium.newLast30d > 0 ? "up" : "flat",
    },
    {
      key: "mrr",
      icon: "payment",
      color: "#FFBC99",
      label: t("home.kpis.mrr", "MRR estimado"),
      value: fmtEur(premium.estimatedMonthlyRevenueEur),
      to: "/subscriptions",
      sub: premium.byPlan
        ? `${premium.byPlan.monthly ?? 0} ${t("home.kpis.monthly", "mens.")} · ${
            premium.byPlan.yearly ?? 0
          } ${t("home.kpis.yearly", "anual")}`
        : null,
      tone: "flat",
    },
    {
      key: "games",
      icon: "bar-chart",
      color: "#FFA5D8",
      label: t("home.kpis.gamesToday", "Partidas hoy"),
      value: fmt(gamesToday),
      spark: spark.length > 1 ? spark : null,
      sub:
        gamesDelta != null
          ? `${gamesDelta >= 0 ? "▲" : "▼"} ${Math.abs(gamesDelta)}% ${t(
              "home.kpis.vsYesterday",
              "vs ayer"
            )}`
          : null,
      tone: gamesDelta == null ? "flat" : gamesDelta >= 0 ? "up" : "down",
    },
    {
      key: "reads",
      icon: "list",
      color: "#B1E5FC",
      label: t("home.kpis.reads", "Lecturas (30 d)"),
      value: fmt(texts.readsLast30d),
      to: "/texts",
      sub:
        texts.readsLast7d != null
          ? `${fmt(texts.readsLast7d)} ${t("home.kpis.readsWeek", "en 7 d")}`
          : null,
      tone: "flat",
    },
    {
      key: "review",
      icon: "edit",
      color: "#FFD88D",
      label: t("home.kpis.review", "Palabras a revisar"),
      value: fmt(content.wordsToReview),
      to: "/cards",
      sub:
        content.wordsToReview > 0
          ? t("home.kpis.reviewContext", "contexto pendiente")
          : t("home.kpis.reviewClear", "todo al día"),
      tone: content.wordsToReview > 0 ? "down" : "up",
    },
  ];

  const [customers, setCustomers] = useState([]);
  useEffect(() => {
    getUsers?.({ take: 6, orderBy: "lastActivity", order: "desc" })
      .then((r) => {
        const list = r?.data?.results || r?.data?.data || [];
        setCustomers(Array.isArray(list) ? list.slice(0, 6) : []);
      })
      .catch(() => setCustomers([]));
  }, []);

  return (
    <Card
      className={cn(styles.card, className)}
      title={t("home.kpis.title", "Resumen")}
      classTitle="title-orange"
      head={
        <div className={styles.note}>
          {t("home.kpis.note", "Datos reales · actualizado cada 5 min")}
        </div>
      }
    >
      <div className={styles.grid}>
        {tiles.map((tile) => {
          const body = (
            <>
              <div className={styles.tileIcon} style={{ backgroundColor: tile.color }}>
                <Icon name={tile.icon} size="20" />
              </div>
              <div className={styles.tileLabel}>{tile.label}</div>
              <div className={styles.tileValue}>
                {loading ? <span className={styles.skeleton} /> : tile.value}
              </div>
              {!loading && tile.spark && (
                <div className={styles.spark}>
                  {tile.spark.map((v, i) => (
                    <span
                      key={i}
                      className={cn({ [styles.sparkLast]: i === tile.spark.length - 1 })}
                      style={{ height: `${Math.max(10, Math.round((v / sparkMax) * 100))}%` }}
                    />
                  ))}
                </div>
              )}
              {!loading && tile.sub && (
                <div className={cn(styles.tileSub, styles[tile.tone])}>{tile.sub}</div>
              )}
            </>
          );
          return tile.to ? (
            <Link className={cn(styles.tile, styles.tileLink)} key={tile.key} to={tile.to}>
              {body}
            </Link>
          ) : (
            <div className={styles.tile} key={tile.key}>
              {body}
            </div>
          );
        })}
      </div>

      <div className={styles.stripe}>
        <div className={styles.text}>
          {loading ? (
            t("home.kpis.loading", "Cargando datos reales…")
          ) : error ? (
            t("home.kpis.error", "No se pudo cargar el resumen")
          ) : (
            <>
              {t("home.kpis.line1", "Tenemos")}{" "}
              <strong>{fmt(users.activeLast30d ?? users.total)} </strong>
              {users.activeLast30d != null
                ? t(
                    "home.kpis.activeSuffix",
                    "usuarios activos (últimos 30 días) en BoostChinese."
                  )
                : t("home.kpis.totalSuffix", "usuarios en BoostChinese.")}
            </>
          )}
        </div>
        <Link className={cn("button-stroke", styles.button)} to="/users">
          {t("home.kpis.viewCustomers", "Ver usuarios")}
        </Link>
      </div>

      {customers.length > 0 && (
        <div className={styles.avatars}>
          {customers.map((u) => {
            const name = u.name || u.username || u.email || "—";
            const first = String(name).split(/\s+/)[0];
            const level = u.onboard?.level === "Begginer" ? "Beginner" : u.onboard?.level;
            return (
              <Link
                to={`/users?search=${encodeURIComponent(u.email || name)}`}
                className={styles.avatarItem}
                key={u._id || u.id || name}
                title={u.email || name}
              >
                <div className={styles.avatarCircle} style={{ background: swatchFor(name) }}>
                  {u.avatar?.small?.location || u.avatar?.small ? (
                    <img src={u.avatar?.small?.location || u.avatar?.small} alt="" />
                  ) : (
                    <span>{initials(name)}</span>
                  )}
                </div>
                <div className={styles.avatarName}>{first}</div>
                {level && (
                  <div className={cn(styles.avatarLevel, styles[`level${level}`])}>{level}</div>
                )}
              </Link>
            );
          })}
          <Link to="/users" className={styles.avatarItem}>
            <div className={cn(styles.avatarCircle, styles.avatarArrow)}>
              <Icon name="arrow-right" size="20" />
            </div>
            <div className={styles.avatarName}>{t("home.kpis.viewAll", "Ver todos")}</div>
          </Link>
        </div>
      )}
    </Card>
  );
};

export default Kpis;
